// Procedural devices for the hero and the Features section, and their studio
// light. Units: 1 = 10 cm. Proportions follow a 14" laptop, a 6.3" phone and
// a 13" tablet, so the real app captures sit on correctly sized displays.
import * as THREE from '../vendor/three/three.module.min.js';
import { RoundedBoxGeometry } from '../vendor/three/addons/RoundedBoxGeometry.js';

export function roundedRectShape(w, h, r, { topOnly = false } = {}) {
  const s = new THREE.Shape();
  const x = -w / 2;
  const y = -h / 2;
  const rb = topOnly ? 0.004 : r;
  s.moveTo(x + rb, y);
  s.lineTo(x + w - rb, y);
  s.quadraticCurveTo(x + w, y, x + w, y + rb);
  s.lineTo(x + w, y + h - r);
  s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h);
  s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + rb);
  s.quadraticCurveTo(x, y, x + rb, y);
  return s;
}

// ShapeGeometry with UVs normalised to the rectangle, so a screenshot maps 1:1.
export function panelGeometry(w, h, r, options) {
  const g = new THREE.ShapeGeometry(roundedRectShape(w, h, r, options), 12);
  const pos = g.attributes.position;
  const uv = g.attributes.uv;
  for (let i = 0; i < pos.count; i++) {
    uv.setXY(i, pos.getX(i) / w + 0.5, pos.getY(i) / h + 0.5);
  }
  uv.needsUpdate = true;
  return g;
}

function extrudedSlab(w, h, depth, r, bevel) {
  const shape = roundedRectShape(w - bevel * 2, h - bevel * 2, Math.max(0.001, r - bevel));
  const g = new THREE.ExtrudeGeometry(shape, {
    depth: depth - bevel * 2,
    bevelEnabled: true,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 6,
    curveSegments: 20,
  });
  g.translate(0, 0, -(depth - bevel * 2) / 2);
  g.computeVertexNormals();
  return g;
}

export function metal(color, roughness = 0.34, { anisotropy = 0 } = {}) {
  return new THREE.MeshPhysicalMaterial({
    color,
    metalness: 1,
    roughness,
    clearcoat: 0.22,
    clearcoatRoughness: 0.32,
    anisotropy,
    anisotropyRotation: Math.PI / 2,
  });
}

const glassBlack = () =>
  new THREE.MeshPhysicalMaterial({
    color: 0x050607,
    metalness: 0,
    roughness: 0.06,
    clearcoat: 1,
    clearcoatRoughness: 0.03,
  });

// The display shows the capture without tone mapping; `uBrightness` lets the
// screen wake up as the lid opens, `uMix` cross-fades to a second capture.
// `mapPast`, `uMask` and `uReveal` let the pointer open a window onto another
// day of the same screen (the Features laptop's time machine).
const noMask = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1);
noMask.needsUpdate = true;
export function screenMaterial(textureA, textureB = null) {
  return new THREE.ShaderMaterial({
    uniforms: {
      mapA: { value: textureA },
      mapB: { value: textureB || textureA },
      uMix: { value: 0 },
      // 0: mapA → mapB crossfade (the hero); 1: a diagonal sweep of light (Features)
      uWipe: { value: 0 },
      uBrightness: { value: 1 },
      mapPast: { value: textureA },
      uMask: { value: noMask },
      uReveal: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform sampler2D mapA;
      uniform sampler2D mapB;
      uniform float uMix;
      uniform float uWipe;
      uniform float uBrightness;
      uniform sampler2D mapPast;
      uniform sampler2D uMask;
      uniform float uReveal;
      varying vec2 vUv;
      void main() {
        vec4 a = texture2D(mapA, vUv);
        vec4 b = texture2D(mapB, vUv);
        float k = smoothstep(0.0, 1.0, uMix);
        float edge = vUv.x * 0.82 + (1.0 - vUv.y) * 0.18;
        float pos = uMix * 1.2 - 0.1;
        if (uWipe > 0.5) k = 1.0 - smoothstep(pos - 0.035, pos + 0.035, edge);
        vec3 c = mix(a.rgb, b.rgb, k);
        if (uWipe > 0.5) {
          // the light that carries the next screen in
          float line = exp(-pow((edge - pos) * 34.0, 2.0)) * smoothstep(0.0, 0.08, uMix) * (1.0 - smoothstep(0.92, 1.0, uMix));
          c += vec3(0.86, 0.9, 0.95) * line * 0.55;
        }
        if (uReveal > 0.0) {
          // where the pointer has been, the other day shows through, with a thin cool rim
          float m = texture2D(uMask, vUv).r * uReveal;
          float k = smoothstep(0.12, 0.62, m);
          float rim = smoothstep(0.04, 0.26, m) * (1.0 - smoothstep(0.26, 0.6, m));
          c = mix(c, texture2D(mapPast, vUv).rgb, k) + vec3(0.36, 0.82, 0.92) * rim * 0.4;
        }
        // glass: a faint sheen toward the top and one soft diagonal reflection
        float sheen = smoothstep(0.55, 1.0, vUv.y) * 0.03;
        float d = vUv.x * 0.62 + vUv.y * 0.78;
        float band = smoothstep(0.34, 0.0, abs(d - 1.02)) * 0.05;
        gl_FragColor = vec4(c * uBrightness + (sheen + band) * uBrightness, 1.0);
        #include <colorspace_fragment>
      }
    `,
    toneMapped: false,
  });
}

export function contactShadow(w, d, opacity = 0.55, { core = 1, mid = 0.55 } = {}) {
  const size = 256;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  const grd = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grd.addColorStop(0, `rgba(20,32,40,${core})`);
  grd.addColorStop(0.42, `rgba(20,32,40,${mid})`);
  grd.addColorStop(1, 'rgba(20,32,40,0)');
  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(c);
  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(w, d),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity, depthWrite: false }),
  );
  m.rotation.x = -Math.PI / 2;
  m.renderOrder = -1;
  return m;
}

/* ------------------------------------------------------------------ */
/* Laptop                                                              */
/* ------------------------------------------------------------------ */
export function createLaptop({ screen, body = 0xbcc1c8, shadowOpacity = 0.6, lidMark = null } = {}) {
  const W = 3.126;
  const D = 2.212;
  const T = 0.105;
  const group = new THREE.Group();
  const aluminium = metal(body, 0.3, { anisotropy: 0.55 });

  const base = new THREE.Mesh(new RoundedBoxGeometry(W, T, D, 5, 0.045), aluminium);
  base.position.y = T / 2;
  group.add(base);

  // keyboard well
  const well = new THREE.Mesh(
    panelGeometry(2.74, 1.16, 0.035),
    new THREE.MeshStandardMaterial({ color: 0x090a0c, roughness: 0.9, metalness: 0.05 }),
  );
  well.rotation.x = -Math.PI / 2;
  well.position.set(0, T + 0.0008, -0.3);
  group.add(well);

  // keys
  // keycaps: slightly domed edges catch the studio light like real ones
  const keyGeo = new RoundedBoxGeometry(1, 0.018, 1, 3, 0.008);
  const keyMat = new THREE.MeshPhysicalMaterial({
    color: 0x16181b,
    roughness: 0.55,
    metalness: 0,
    clearcoat: 0.35,
    clearcoatRoughness: 0.5,
    envMapIntensity: 0.7,
  });
  const rows = [
    { h: 0.085, keys: Array(14).fill(0.172) },
    { h: 0.16, keys: [...Array(13).fill(0.172), 0.27] },
    { h: 0.16, keys: [0.27, ...Array(13).fill(0.172)] },
    { h: 0.16, keys: [0.315, ...Array(11).fill(0.172), 0.315] },
    { h: 0.16, keys: [0.405, ...Array(10).fill(0.172), 0.405] },
    { h: 0.16, keys: [0.172, 0.172, 0.172, 0.215, 0.955, 0.215, 0.172, 0.172, 0.172] },
  ];
  const gap = 0.022;
  const placements = [];
  let z = -0.3 - 1.16 / 2 + 0.07;
  for (const row of rows) {
    const total = row.keys.reduce((a, k) => a + k, 0) + gap * (row.keys.length - 1);
    let x = -total / 2;
    for (const k of row.keys) {
      placements.push({ x: x + k / 2, z: z + row.h / 2, w: k, d: row.h });
      x += k + gap;
    }
    z += row.h + gap;
  }
  const keys = new THREE.InstancedMesh(keyGeo, keyMat, placements.length);
  const m4 = new THREE.Matrix4();
  placements.forEach((p, i) => {
    m4.compose(
      new THREE.Vector3(p.x, T + 0.01, p.z),
      new THREE.Quaternion(),
      new THREE.Vector3(p.w, 1, p.d),
    );
    keys.setMatrixAt(i, m4);
  });
  group.add(keys);

  // trackpad: a hairline gap around a slightly smoother glass surface
  const padGap = new THREE.Mesh(
    panelGeometry(1.316, 0.836, 0.05),
    new THREE.MeshStandardMaterial({ color: 0x6f747b, roughness: 0.6, metalness: 0.6 }),
  );
  padGap.rotation.x = -Math.PI / 2;
  padGap.position.set(0, T + 0.0006, 0.64);
  group.add(padGap);
  const pad = new THREE.Mesh(panelGeometry(1.3, 0.82, 0.045), metal(0xc3c7cd, 0.16));
  pad.rotation.x = -Math.PI / 2;
  pad.position.set(0, T + 0.0012, 0.64);
  group.add(pad);

  // hinge
  const hinge = new THREE.Mesh(
    new THREE.CylinderGeometry(0.052, 0.052, 2.55, 24),
    new THREE.MeshStandardMaterial({ color: 0x1a1c1f, roughness: 0.45, metalness: 0.8 }),
  );
  hinge.rotation.z = Math.PI / 2;
  hinge.position.set(0, T + 0.01, -D / 2 + 0.04);
  group.add(hinge);

  // lid, pivoting on the hinge
  const lid = new THREE.Group();
  lid.position.set(0, T + 0.012, -D / 2 + 0.035);
  group.add(lid);
  const LD = 2.18;
  const lidShell = new THREE.Mesh(new RoundedBoxGeometry(W, 0.042, LD, 5, 0.02), aluminium);
  lidShell.position.set(0, 0.021, LD / 2);
  lid.add(lidShell);

  // the wordmark polished into the outside of the lid, like an engraved logo
  if (lidMark) {
    const markW = 0.78;
    const mark = new THREE.Mesh(
      new THREE.PlaneGeometry(markW, markW * (lidMark.image.height / lidMark.image.width)),
      new THREE.MeshPhysicalMaterial({
        color: 0x8d949c,
        metalness: 1,
        roughness: 0.12,
        clearcoat: 0.6,
        clearcoatRoughness: 0.1,
        alphaMap: lidMark,
        transparent: true,
        depthWrite: false,
      }),
    );
    // readable from the front while the lid is closed, which is how the page shows it
    mark.rotation.x = -Math.PI / 2;
    mark.position.set(0, 0.0432, LD / 2);
    lid.add(mark);
  }

  const bezel = new THREE.Mesh(panelGeometry(W - 0.03, LD - 0.03, 0.05), glassBlack());
  bezel.rotation.x = Math.PI / 2;
  bezel.position.set(0, -0.0012, LD / 2);
  lid.add(bezel);

  const displayW = 3.025;
  const displayH = 1.964;
  const screenMat = screenMaterial(screen || null);
  const display = new THREE.Mesh(panelGeometry(displayW, displayH, 0.07, { topOnly: true }), screenMat);
  display.rotation.x = Math.PI / 2;
  display.position.set(0, -0.0024, LD - 0.052 - displayH / 2);
  lid.add(display);

  const notch = new THREE.Mesh(panelGeometry(0.2, 0.034, 0.012), new THREE.MeshBasicMaterial({ color: 0x000000 }));
  notch.rotation.x = Math.PI / 2;
  notch.position.set(0, -0.0034, LD - 0.052 - 0.017 + 0.004);
  lid.add(notch);

  const shadow = contactShadow(W * 1.7, D * 1.9, shadowOpacity, { core: 0.9, mid: 0.4 });
  shadow.position.y = 0.001;
  group.add(shadow);

  const api = {
    group,
    lid,
    screenMaterial: screenMat,
    display,
    // angle in radians: 0 closed, ~1.9 comfortably open
    setOpen(angle) {
      lid.rotation.x = -angle;
    },
    // centre of the open display in group space, useful for camera framing
    screenCenter(angle = 1.85) {
      const v = new THREE.Vector3(0, 0, LD - 0.052 - displayH / 2);
      v.applyAxisAngle(new THREE.Vector3(1, 0, 0), -angle);
      return v.add(lid.position);
    },
  };
  api.setOpen(1.85);
  return api;
}

/* ------------------------------------------------------------------ */
/* Phone                                                               */
/* ------------------------------------------------------------------ */
export function createPhone({ screen, body = 0xc9ccd1 } = {}) {
  const W = 0.7185;
  const H = 1.5;
  const Dp = 0.0875;
  const group = new THREE.Group();
  const frame = metal(body, 0.3);
  const shell = new THREE.Mesh(extrudedSlab(W, H, Dp, 0.118, 0.022), frame);
  group.add(shell);

  const front = new THREE.Mesh(panelGeometry(W - 0.01, H - 0.01, 0.112), glassBlack());
  front.position.z = Dp / 2 + 0.0006;
  group.add(front);

  const screenMat = screenMaterial(screen || null);
  const display = new THREE.Mesh(panelGeometry(0.6687, 1.454, 0.093), screenMat);
  display.position.z = Dp / 2 + 0.0014;
  group.add(display);

  // back glass and the full-width camera plateau
  const back = new THREE.Mesh(
    panelGeometry(W - 0.01, H - 0.01, 0.112),
    new THREE.MeshPhysicalMaterial({ color: body, metalness: 0.2, roughness: 0.55, clearcoat: 0.6 }),
  );
  back.rotation.y = Math.PI;
  back.position.z = -Dp / 2 - 0.0006;
  group.add(back);
  const plateau = new THREE.Mesh(new RoundedBoxGeometry(W - 0.03, 0.3, 0.03, 4, 0.012), frame);
  plateau.position.set(0, H / 2 - 0.19, -Dp / 2 - 0.012);
  group.add(plateau);
  const lensMat = new THREE.MeshPhysicalMaterial({ color: 0x0a0c10, metalness: 0.4, roughness: 0.08, clearcoat: 1 });
  const ringMat = metal(0x9aa0a8, 0.25);
  [
    [-0.2, H / 2 - 0.12],
    [-0.2, H / 2 - 0.26],
    [-0.07, H / 2 - 0.19],
  ].forEach(([x, y]) => {
    const ring = new THREE.Mesh(new THREE.CylinderGeometry(0.062, 0.062, 0.02, 32), ringMat);
    ring.rotation.x = Math.PI / 2;
    ring.position.set(x, y, -Dp / 2 - 0.035);
    group.add(ring);
    const lens = new THREE.Mesh(new THREE.CylinderGeometry(0.046, 0.046, 0.022, 32), lensMat);
    lens.rotation.x = Math.PI / 2;
    lens.position.set(x, y, -Dp / 2 - 0.037);
    group.add(lens);
  });

  // side buttons
  const btn = (w, h, x, y) => {
    const b = new THREE.Mesh(new RoundedBoxGeometry(0.012, h, w, 2, 0.005), frame);
    b.position.set(x, y, 0);
    group.add(b);
  };
  btn(0.03, 0.07, -W / 2 - 0.004, 0.42);
  btn(0.03, 0.12, -W / 2 - 0.004, 0.26);
  btn(0.03, 0.12, -W / 2 - 0.004, 0.11);
  btn(0.03, 0.16, W / 2 + 0.004, 0.24);
  btn(0.03, 0.1, W / 2 + 0.004, -0.12);

  return { group, screenMaterial: screenMat, display };
}

/* ------------------------------------------------------------------ */
/* Tablet (landscape)                                                  */
/* ------------------------------------------------------------------ */
export function createTablet({ screen, body = 0xbfc3c9 } = {}) {
  const W = 2.816;
  const H = 2.155;
  const Dt = 0.051;
  const group = new THREE.Group();
  const frame = metal(body, 0.34);
  group.add(new THREE.Mesh(extrudedSlab(W, H, Dt, 0.17, 0.014), frame));
  const front = new THREE.Mesh(panelGeometry(W - 0.012, H - 0.012, 0.165), glassBlack());
  front.position.z = Dt / 2 + 0.0006;
  group.add(front);
  const screenMat = screenMaterial(screen || null);
  const display = new THREE.Mesh(panelGeometry(2.64, 1.98, 0.11), screenMat);
  display.position.z = Dt / 2 + 0.0014;
  group.add(display);
  const cam = new THREE.Mesh(
    new THREE.CircleGeometry(0.012, 20),
    new THREE.MeshBasicMaterial({ color: 0x1a2330 }),
  );
  cam.position.set(0, H / 2 - 0.044, Dt / 2 + 0.0016);
  group.add(cam);
  return { group, screenMaterial: screenMat, display };
}

// Reflections for the metal: a product studio rather than a furnished room.
// A dark floor, a soft grey sweep and a few softboxes give the aluminium the
// gradients and highlight bands of a photographed laptop; an evenly bright
// room turns a silver lid into a flat white slab.
function studioScene({ strip = 3.0 } = {}) {
  const scene = new THREE.Scene();
  const sweep = new THREE.Mesh(
    new THREE.SphereGeometry(40, 64, 32),
    new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      vertexShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          vDir = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          float y = normalize(vDir).y;
          vec3 floorC = vec3(0.05, 0.055, 0.06);
          vec3 horizon = vec3(0.3, 0.32, 0.34);
          vec3 top = vec3(0.8, 0.84, 0.88);
          vec3 c = y < 0.0
            ? mix(horizon * 0.5, floorC, smoothstep(0.0, -0.4, y))
            : mix(horizon, top, smoothstep(0.02, 0.85, y));
          gl_FragColor = vec4(c, 1.0);
        }
      `,
    }),
  );
  scene.add(sweep);
  const softbox = (w, h, intensity, x, y, z) => {
    const m = new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      new THREE.MeshBasicMaterial({ color: new THREE.Color().setScalar(intensity), side: THREE.DoubleSide }),
    );
    m.position.set(x, y, z);
    m.lookAt(0, 0, 0);
    scene.add(m);
  };
  softbox(30, 16, 2.2, -6, 26, 8); // overhead key
  softbox(44, 5, strip, 0, 13, -27); // strip behind: the band of light across a closed lid
  softbox(5, 34, 1.5, -30, 10, 6); // left strip, for the edges
  softbox(5, 34, 1.1, 30, 10, -4); // right strip
  return scene;
}

export async function createStudio(
  renderer,
  scene,
  { rim = 0x7aa471, rimIntensity = 1.6, envIntensity = 0.34, keyIntensity = 1.35, fillIntensity = 0.5, fillColor = 0x9fb1c7, strip } = {},
) {
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(studioScene({ strip }), 0.03).texture;
  pmrem.dispose();
  scene.environmentIntensity = envIntensity;

  const key = new THREE.DirectionalLight(0xfff6ec, keyIntensity);
  key.position.set(-4, 7, 6);
  scene.add(key);
  const fill = new THREE.DirectionalLight(fillColor, fillIntensity);
  fill.position.set(5, 2, 4);
  scene.add(fill);
  const back = new THREE.DirectionalLight(rim, rimIntensity);
  back.position.set(0, 0.8, -6);
  scene.add(back);
  return { key, fill, back };
}

// Opened from disk (file://), browsers refuse images as WebGL textures. The
// build writes the captures as data URLs to dist/captures-inline.js, which is
// only fetched in that case.
// Pages below the site root (en/) set <html data-root="../">; the scripts keep
// naming assets from the root.
const ROOT = document.documentElement.dataset.root || '';
let inlineCaptures = null;
function textureSource(url) {
  if (location.protocol !== 'file:') return Promise.resolve(ROOT + url);
  inlineCaptures ??= new Promise((resolve) => {
    if (window.__TB_CAPTURES) return resolve(window.__TB_CAPTURES);
    const script = document.createElement('script');
    script.src = `${ROOT}dist/captures-inline.js`;
    script.onload = () => resolve(window.__TB_CAPTURES || {});
    script.onerror = () => resolve({});
    document.head.appendChild(script);
  });
  return inlineCaptures.then((map) => map[url] || ROOT + url);
}

export async function loadTexture(renderer, url) {
  const src = await textureSource(url);
  return new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(
      src,
      (t) => {
        t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
        t.generateMipmaps = true;
        t.minFilter = THREE.LinearMipmapLinearFilter;
        resolve(t);
      },
      undefined,
      reject,
    );
  });
}
